import React, { useEffect, useRef, useCallback } from 'react';
import { EditorView, basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import { lintGutter, setDiagnostics } from '@codemirror/lint';
import type { Diagnostic } from '@codemirror/lint';
import { keymap } from '@codemirror/view';
import { Prec } from '@codemirror/state';
import { Button, Space, Typography, Tooltip } from 'antd';
import { FormatPainterOutlined, InfoCircleOutlined, BoldOutlined } from '@ant-design/icons';
import { toggleBold } from './toggleBold';

const { Text } = Typography;

/**
 * Tries to extract a {from, to} character range from a JS engine JSON parse
 * error message. Returns null if no position info is present.
 *
 * Handles:
 *   V8:      "… at position 42"
 *   Firefox: "… at line 5 column 2 …"
 */
export function parseJsonErrorPosition(
  errorMsg: string,
  docText: string,
): { from: number; to: number } | null {
  // V8: "Unexpected token X in JSON at position 42"
  const posMatch = errorMsg.match(/at position (\d+)/i);
  if (posMatch) {
    const pos = Math.min(parseInt(posMatch[1], 10), docText.length);
    return { from: pos, to: Math.min(pos + 1, docText.length) };
  }

  // Firefox: "JSON.parse: unexpected character at line 5 column 2 of the JSON data"
  const lineColMatch = errorMsg.match(/at line (\d+) column (\d+)/i);
  if (lineColMatch) {
    const targetLine = parseInt(lineColMatch[1], 10);
    const targetCol = parseInt(lineColMatch[2], 10);
    const lines = docText.split('\n');
    let offset = 0;
    for (let i = 0; i < targetLine - 1 && i < lines.length; i++) {
      offset += lines[i].length + 1;
    }
    offset += Math.min(targetCol - 1, (lines[targetLine - 1] ?? '').length);
    return { from: offset, to: Math.min(offset + 1, docText.length) };
  }

  return null;
}

const HINT = (
  <div style={{ fontSize: 11, lineHeight: 1.6 }}>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>Valid card types</div>
    <div><strong>Steps:</strong> intro, insight, strategy, mentor, summary, challenge, comic, mindmap, stats, reveal, voices, timeline, reality_check, speaker, step_chat</div>
    <div style={{ marginTop: 4 }}><strong>Tests:</strong> scenario, chat_simulator, story_mode, quick_fire, before_after, drag_rank, swipe_test</div>
  </div>
);

const CM_THEME = EditorView.theme({
  '&': {
    border: '1px solid #d9d9d9',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace',
    // Cap the editor so a long body scrolls inside it instead of inflating
    // the modal (which used to push the toolbar / Bold button out of view).
    maxHeight: '90vh',
  },
  '&.cm-focused': {
    outline: 'none',
    border: '1px solid #4096ff',
    boxShadow: '0 0 0 2px rgba(5, 145, 255, 0.1)',
  },
  '.cm-content': {
    minHeight: '224px',
    lineHeight: '1.6',
    padding: '8px 4px',
  },
  '.cm-scroller': { overflow: 'auto' },
  '.cm-gutters': { borderRadius: '6px 0 0 6px' },
});

interface LessonBodyEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: string | null;
}

const LessonBodyEditor: React.FC<LessonBodyEditorProps> = ({ value = '', onChange, error }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const applyBold = useCallback((view: EditorView) => {
    const { from, to } = view.state.selection.main;
    const result = toggleBold(view.state.doc.toString(), from, to);
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: result.text },
      selection: { anchor: result.selFrom, head: result.selTo },
    });
    view.focus();
  }, []);
  const applyBoldRef = useRef(applyBold);
  applyBoldRef.current = applyBold;

  // Mount editor once
  useEffect(() => {
    if (!containerRef.current) return;

    const view = new EditorView({
      doc: value,
      extensions: [
        basicSetup,
        Prec.highest(
          keymap.of([
            {
              key: 'Mod-b',
              run: (view) => {
                applyBoldRef.current(view);
                return true;
              },
            },
          ]),
        ),
        json(),
        lintGutter(),
        CM_THEME,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current?.(update.state.doc.toString());
          }
        }),
      ],
      parent: containerRef.current,
    });

    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. Format button, opening a different lesson)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
      });
    }
  }, [value]);

  // Push / clear diagnostics when error prop changes
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    if (!error) {
      view.dispatch(setDiagnostics(view.state, []));
      return;
    }

    const docText = view.state.doc.toString();
    const pos = parseJsonErrorPosition(error, docText);

    const diagnostic: Diagnostic = {
      from: pos?.from ?? 0,
      to: pos?.to ?? Math.min(1, docText.length),
      severity: 'error',
      message: error,
    };

    view.dispatch(setDiagnostics(view.state, [diagnostic]));
  }, [error]);

  const handleFormat = useCallback(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (!current.trim()) return;
    try {
      const formatted = JSON.stringify(JSON.parse(current), null, 2);
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: formatted },
      });
    } catch {
      // ignore — the error will be shown when the user tries to save
    }
  }, []);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            LessonModule JSON — must contain a <code>cards</code> array
          </Text>
          <Tooltip title={HINT} overlayStyle={{ maxWidth: 320 }}>
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
          </Tooltip>
        </Space>
        <Space size={4}>
          <Tooltip title="Bold selected text (Ctrl/Cmd+B)">
            <Button
              size="small"
              icon={<BoldOutlined />}
              onClick={() => {
                if (viewRef.current) applyBold(viewRef.current);
              }}
            />
          </Tooltip>
          <Button size="small" icon={<FormatPainterOutlined />} onClick={handleFormat}>
            Format
          </Button>
        </Space>
      </div>

      <div ref={containerRef} />
    </Space>
  );
};

export default LessonBodyEditor;
